import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function InternationalBuyerPage() {
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'International Buyer Guide', item: import.meta.env.VITE_SITE_URL + '/blog/vigorbuy-spreadsheet-international-buyer-guide' },
    ],
  };
  return (
    <>
      <Navbar />
      <main className="pt-24 pb-16">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org', '@type': 'Article',
          headline: 'The International Buyer Guide to Vigorbuy Spreadsheet Shopping in 2026',
          description: 'Essential tips for international shoppers using the Vigorbuy Spreadsheet — covering shipping, customs, sizing conversions, and regional considerations.',
          author: { '@type': 'Organization', name: 'Vigorbuy Spreadsheet Hub' },
          datePublished: '2026-07-13', dateModified: '2026-07-22',
          publisher: { '@type': 'Organization', name: 'Vigorbuy Spreadsheet Hub', url: import.meta.env.VITE_SITE_URL },
          mainEntityOfPage: { '@type': 'WebPage', '@id': import.meta.env.VITE_SITE_URL + '/blog/vigorbuy-spreadsheet-international-buyer-guide' },
        }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">Home</Link></li>
              <li><span className="text-foreground-300">/</span></li>
              <li><span className="text-foreground-700 font-medium">International Buyer Guide</span></li>
            </ol>
          </nav>
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Guide</span>
              <time className="text-xs text-foreground-400">July 13, 2026</time>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              The International Buyer Guide to Vigorbuy Spreadsheet Shopping
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              Shopping internationally through the Vigorbuy Spreadsheet opens up access to an incredible range of
              products, but it also introduces considerations that domestic shoppers never face. Understanding shipping
              logistics, customs procedures, regional sizing differences, and payment nuances will help you navigate
              international purchases with confidence.
            </p>
          </header>
          <div className="prose-custom space-y-8">
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Understanding Shipping and Logistics</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                International shipping through spreadsheet-accessible platforms varies significantly by destination
                country and shipping method. The Vigorbuy Spreadsheet community provides valuable region-specific
                shipping experiences that help set realistic expectations. Pay attention to estimated delivery windows
                shared by buyers in your country or region — these crowd-sourced timelines are often more accurate
                than official carrier estimates because they account for real-world customs processing and local
                delivery variables.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                Customs duties and import taxes are another critical consideration for international buyers. While the
                Vigorbuy Spreadsheet itself does not handle transactions, many community members share their experiences
                with customs charges for specific product types and values. As a general rule, research your country's
                de minimis threshold — the value below which imports are exempt from duties — before placing larger
                orders. Consolidating multiple small purchases into a single shipment can sometimes trigger customs
                charges that would have been avoided with separate shipments.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Package tracking is essential for international orders. Always ensure your chosen shipping method includes
                tracking, and monitor your shipment's progress through customs. Delays at customs are common and usually
                resolve within a few days, but tracking visibility lets you identify when intervention might be needed.
                The Vigorbuy Spreadsheet community forums are excellent resources for troubleshooting shipping issues
                specific to your destination country.
              </p>
            </section>
            <div className="p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200/70 text-center">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">Shop Globally with Confidence</h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">Start your international shopping journey on the Vigorbuy Spreadsheet.</p>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                Explore Now <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}